import React, { useState } from 'react';
import patientService from '../../services/patientService';
import PatientDocuments from './PatientDocuments';
import PatientTreatments from './PatientTreatments';
import AppointmentForm from '../appointments/AppointmentForm';

const PatientDetail = ({ patient, onClose, onEdit, onSuccess, onAddTreatment }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);

  // Safety check: if patient is not provided, show error
  if (!patient) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <p className="text-center text-gray-600">No patient data available</p>
          <button
            onClick={onClose}
            className="mt-4 w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const handleScheduleAppointment = () => {
    setShowAppointmentForm(true);
  };

  const handleAppointmentSuccess = () => {
    setShowAppointmentForm(false);
    // Optionally refresh patient data if needed
    onSuccess();
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this patient? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      await patientService.deletePatient(patient.id);
      alert('Patient deleted successfully');
      onSuccess();
      onClose();
    } catch (error) {
      alert('Failed to delete patient');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setLoading(true);
    try {
      await patientService.updatePatientStatus(patient.id, newStatus);
      alert('Patient status updated successfully');
      onSuccess();
      onClose();
    } catch (error) {
      alert('Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Completed':
        return 'bg-blue-100 text-blue-800';
      case 'Follow-up Pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateDaysRemaining = () => {
    if (!patient.end_date) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(patient.end_date);
    endDate.setHours(0, 0, 0, 0);
    const daysRemaining = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    
    return daysRemaining;
  };

  const calculateTreatmentDuration = () => {
    if (!patient.start_date || !patient.end_date) return null;
    
    const start = new Date(patient.start_date);
    const end = new Date(patient.end_date);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(days / 7);
    const remainingDays = days % 7;
    
    if (weeks > 0 && remainingDays > 0) {
      return `${weeks} week${weeks > 1 ? 's' : ''} and ${remainingDays} day${remainingDays > 1 ? 's' : ''}`;
    } else if (weeks > 0) {
      return `${weeks} week${weeks > 1 ? 's' : ''}`;
    } else {
      return `${days} day${days > 1 ? 's' : ''}`;
    }
  };

  const renderDaysRemainingBadge = () => {
    if (patient.status !== 'Active' || !patient.end_date) return null;
    
    const daysRemaining = calculateDaysRemaining();
    
    if (daysRemaining === null) return null;
    
    if (daysRemaining > 0) {
      return (
        <p className="text-sm text-blue-600 mt-1">
          📅 {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining
        </p>
      );
    } else if (daysRemaining === 0) {
      return (
        <p className="text-sm text-orange-600 mt-1 font-semibold">
          ⚠️ Treatment ends today
        </p>
      );
    } else {
      return (
        <p className="text-sm text-red-600 mt-1 font-semibold">
          🚨 Overdue by {Math.abs(daysRemaining)} day{Math.abs(daysRemaining) !== 1 ? 's' : ''}
        </p>
      );
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{patient.name}</h2>
              <span
                className={`mt-1 inline-block px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(
                  patient.status
                )}`}
              >
                {patient.status}
              </span>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 bg-gray-50">
            <div className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('info')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'info'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                📋 Patient Information
              </button>
              <button
                onClick={() => setActiveTab('treatments')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'treatments'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                💊 Treatments ({patient.treatments_count || 0})
              </button>
              <button
                onClick={() => setActiveTab('documents')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'documents'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                📄 Documents ({patient.documents_count || 0})
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {activeTab === 'info' && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Contact Information */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      Contact Information
                    </h3>
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm text-gray-600">Phone:</span>
                        <p className="font-medium">{patient.phone}</p>
                      </div>
                      {patient.email && (
                        <div>
                          <span className="text-sm text-gray-600">Email:</span>
                          <p className="font-medium">{patient.email}</p>
                        </div>
                      )}
                      {patient.address && (
                        <div>
                          <span className="text-sm text-gray-600">Address:</span>
                          <p className="font-medium">{patient.address}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Personal Information */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      Personal Information
                    </h3>
                    <div className="space-y-2">
                      {patient.age && (
                        <div>
                          <span className="text-sm text-gray-600">Age:</span>
                          <p className="font-medium">{patient.age} years</p>
                        </div>
                      )}
                      {patient.gender && (
                        <div>
                          <span className="text-sm text-gray-600">Gender:</span>
                          <p className="font-medium">{patient.gender}</p>
                        </div>
                      )}
                      {patient.date_of_birth && (
                        <div>
                          <span className="text-sm text-gray-600">Date of Birth:</span>
                          <p className="font-medium">{patient.date_of_birth}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Medical Information */}
                  <div className="bg-gray-50 p-4 rounded-lg md:col-span-2">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      Medical Information
                    </h3>
                    <div className="space-y-3">
                      {patient.condition && (
                        <div>
                          <span className="text-sm text-gray-600">Condition:</span>
                          <p className="font-medium">{patient.condition}</p>
                        </div>
                      )}
                      {patient.medical_history && (
                        <div>
                          <span className="text-sm text-gray-600">Medical History:</span>
                          <p className="font-medium whitespace-pre-wrap">
                            {patient.medical_history}
                          </p>
                        </div>
                      )}
                      {patient.reference && (
                        <div>
                          <span className="text-sm text-gray-600">Reference / How they found us:</span>
                          <p className="font-medium">{patient.reference}</p>
                        </div>
                      )}
                      
                      {/* Treatment Dates Section */}
                      {(patient.start_date || patient.end_date) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 mt-3 border-t border-gray-200">
                          {patient.start_date && (
                            <div>
                              <span className="text-sm text-gray-600">Treatment Start Date:</span>
                              <p className="font-medium text-indigo-700">{patient.start_date}</p>
                            </div>
                          )}
                          {patient.end_date && (
                            <div>
                              <span className="text-sm text-gray-600">Expected End Date:</span>
                              <p className="font-medium text-indigo-700">{patient.end_date}</p>
                              {renderDaysRemainingBadge()}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Treatment Duration */}
                      {patient.start_date && patient.end_date && (
                        <div className="pt-2">
                          <span className="text-sm text-gray-600">Total Treatment Duration:</span>
                          <p className="font-medium text-green-700">{calculateTreatmentDuration()}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Statistics */}
                  <div className="bg-gray-50 p-4 rounded-lg md:col-span-2">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      Statistics
                    </h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-indigo-600">
                          {patient.appointments_count || 0}
                        </p>
                        <p className="text-sm text-gray-600">Appointments</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-600">
                          {patient.treatments_count || 0}
                        </p>
                        <p className="text-sm text-gray-600">Treatments</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">
                          {patient.documents_count || 0}
                        </p>
                        <p className="text-sm text-gray-600">Documents</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-6 border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Quick Actions
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => onEdit(patient)}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                    >
                      Edit Information
                    </button>
                    
                    <button
                      onClick={handleScheduleAppointment}
                      className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                    >
                      Schedule Appointment
                    </button>
                    
                    {patient.status !== 'Active' && (
                      <button
                        onClick={() => handleStatusChange('Active')}
                        disabled={loading}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                      >
                        Mark as Active
                      </button>
                    )}
                    
                    {patient.status !== 'Completed' && (
                      <button
                        onClick={() => handleStatusChange('Completed')}
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        Mark as Completed
                      </button>
                    )}
                    
                    {patient.status !== 'Follow-up Pending' && (
                      <button
                        onClick={() => handleStatusChange('Follow-up Pending')}
                        disabled={loading}
                        className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50"
                      >
                        Mark Follow-up Pending
                      </button>
                    )}
                    
                    <button
                      onClick={handleDelete}
                      disabled={loading}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                    >
                      Delete Patient
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'treatments' && (
              <PatientTreatments 
                patientId={patient.id} 
                onAddTreatment={() => onAddTreatment(patient)}
              />
            )}

            {activeTab === 'documents' && (
              <PatientDocuments patient={patient} />
            )}
          </div>
        </div>
      </div>

      {/* Appointment Form Modal - Rendered on top of patient detail */}
      {showAppointmentForm && (
        <AppointmentForm
          appointment={null}
          preSelectedPatient={patient}
          onClose={() => setShowAppointmentForm(false)}
          onSuccess={handleAppointmentSuccess}
        />
      )}
    </>
  );
};

export default PatientDetail;